// Work instructions (L3): every assembly revision's instructions, those needing review first.
import { Link } from 'react-router';

import type { InstructionStatus } from '../../api/generated/InstructionStatus';
import { useInstructionSets } from '../../api/queries';
import { Badge, EmptyState, ErrorPanel, Loading, PageHeader } from '../../design/components';
import { formatDateTime } from '../../design/format';

const STATUS: Record<InstructionStatus, { label: string; tone: 'pending' | 'success' }> = {
    Draft: { label: 'Draft', tone: 'pending' },
    NeedsReview: { label: 'Needs review', tone: 'pending' },
    Reviewed: { label: 'Reviewed', tone: 'success' },
};

export function StatusBadge({ status }: { status: InstructionStatus }) {
    return <Badge tone={STATUS[status].tone}>{STATUS[status].label}</Badge>;
}

export function InstructionsPage() {
    const sets = useInstructionSets();
    return (
        <section className="sf-stack" aria-labelledby="instructions-heading">
            <PageHeader
                id="instructions-heading"
                title="Work instructions"
                description="Open a build from the floor overview and choose “Write instructions” to start a set for its revision."
            />
            {sets.isPending ? <Loading what="the instructions" /> : null}
            {sets.isError ? <ErrorPanel error={sets.error} /> : null}
            {sets.data?.length === 0 ? (
                <EmptyState>No instructions yet. Builds follow the steps generated from their MBOM.</EmptyState>
            ) : null}
            {sets.data && sets.data.length > 0 ? (
                <table className="sf-table">
                    <thead>
                        <tr>
                            <th scope="col">Assembly revision</th>
                            <th scope="col">Status</th>
                            <th scope="col">Steps</th>
                            <th scope="col">Last changed</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sets.data.map((set) => (
                            <tr key={set.instructionSetId}>
                                <td>
                                    <Link to={`/lead/instructions/${set.instructionSetId}`} className="sf-mono">
                                        {set.assemblyPartNumber}
                                    </Link>{' '}
                                    {set.assemblyName ?? ''}{' '}
                                    {set.revision ? <span className="sf-muted">rev {set.revision}</span> : null}
                                </td>
                                <td>
                                    <StatusBadge status={set.status} />
                                </td>
                                <td>{set.stepCount}</td>
                                <td>
                                    {formatDateTime(set.updated.at)}{' '}
                                    <span className="sf-muted">by {set.updated.displayName}</span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : null}
        </section>
    );
}
